import { HelpCircle, Wifi, WifiOff } from 'lucide-react';
import {
  CONNECTEE, DECONNECTEE, SANS_CONTACT,
  ageJoignabilite, etatJoignabilite, libelleJoignabilite, titreJoignabilite,
} from '@/shared/utils/liveness';

/**
 * Pastille de joignabilité : deux états visibles, plus un cas de repli.
 *
 * `connectee` et `deconnectee` sont les deux seuls états qu'un exploitant rencontre.
 * `sans_contact` reste à part parce qu'il n'appelle pas le même geste : une machine
 * tombée ce matin se règle avec le client, une machine dont aucun contact n'est
 * enregistré se règle avec le fournisseur.
 *
 * `deconnectee` porte toujours l'ancienneté : « déconnectée » sans durée ne dit pas s'il
 * faut appeler le client ou attendre trente secondes. Cette durée est désormais vraie
 * même après un redémarrage du serveur, la date venant de la base.
 */
const STYLE = {
  [CONNECTEE]: { texte: 'text-emerald-600', point: 'bg-emerald-500', Icone: Wifi },
  [DECONNECTEE]: { texte: 'text-red-600', point: 'bg-red-500', Icone: WifiOff },
  [SANS_CONTACT]: { texte: 'text-neutral-500', point: 'bg-neutral-400', Icone: HelpCircle },
};
const INCONNU = { texte: 'text-neutral-400', point: 'bg-neutral-300', Icone: HelpCircle };

/**
 * @param {object} asset   actif (ou son bloc monitoring) — sert à déduire l'état
 * @param {string} etat    état déjà déduit, prioritaire sur `asset`
 * @param {number} ageS    ancienneté en secondes
 * @param {string} genre   'f' (batterie) · 'm' (PAC, ballon, onduleur)
 * @param {string} forme   'icone' (carte, ligne) · 'point' (panneau de pilotage)
 */
export default function ConnexionBadge({
  asset, etat, ageS, genre = 'f', forme = 'icone', className = '', testId,
}) {
  const resolu = etat ?? etatJoignabilite(asset);
  const age = ageS ?? ageJoignabilite(asset);
  const style = STYLE[resolu] || INCONNU;
  const { Icone } = style;

  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium ${style.texte} ${className}`}
      title={titreJoignabilite(resolu, age)}
      data-testid={testId}
    >
      {forme === 'point'
        ? <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.point}`} />
        : <Icone className="w-3 h-3 flex-shrink-0" />}
      <span>{libelleJoignabilite(resolu, age, genre)}</span>
    </span>
  );
}
